// src/hooks/usePDFPreview.js
import { pdf } from '@react-pdf/renderer';
import React from 'react';
import Swal from 'sweetalert2';
import api from '../adapters/api';
import { escapeHtml, icon, optionCard, actionButton, setQtMsg, ensureQtPopupCss } from '../utils/qtPopupUi';

// มือถือ/แท็บเล็ตแสดง PDF ใน <iframe> ไม่ได้ (iOS เห็นแค่หน้าแรก, Android ไม่แสดงเลย)
// → หน้าตัวอย่างบนมือถือแสดงเป็นรูปแทน
const isTouchDevice = () =>
  typeof window !== 'undefined' && window.matchMedia?.('(pointer: coarse)').matches;

// ดึงข้อมูลบิล + สร้าง PDF blob + ตั้งชื่อไฟล์ (ใช้ร่วมกันระหว่างเปิด PDF และส่งเป็นรูป)
// คืน { blob, filename: "QTxxxx ชื่อลูกค้า.pdf", baseName: "QTxxxx ชื่อลูกค้า" }
export async function buildQuotationPdf(docNo, DocumentComponent, previewData = null) {
  const t0 = performance.now();
  let data;
  if (previewData) {
    data = previewData;
  } else {
    const response = await api.post(
      '/api/preview-qt',
      { doc_no: docNo },
      { headers: { 'Content-Type': 'application/json' } }
    );
    data = response.data;
  }
  const t1 = performance.now();

  // Thai dd/MM/YYYY — คัดลอก header ไม่แก้ของเดิม เพราะ previewData อาจถูกใช้ซ้ำ
  const header = {
    ...data.header,
    doc_date: new Date(data.header.doc_date).toLocaleDateString('th-TH', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }),
  };

  // We use React.createElement so that we can pass props into DocumentComponent
  const blob = await pdf(
    React.createElement(DocumentComponent, {
      cartHeader: header,
      customer: data.customer,
      doc_no: data.doc_no,
      salesperson: data.salesperson,
      branchInfo: data.branch,
      companyProfile: {
        company_name_1: data.company.name,
        tax_number: data.company.taxNumber,
        email: data.company.email,
        logoUrl: data.company.logoUrl,
      },
      items: data.details.map(d => ({
        item_code:   d.item_code,
        item_name:   d.item_name,
        qty:         d.qty,
        unit_code:   d.unit_code,
        price:       d.price,
      })),
      discount:     header.discount || 0,
      shippingCost: data.totals.shippingCost || 0,
    })
  ).toBlob();
  console.info(`[qt-pdf] ${docNo}: ดึงข้อมูล ${Math.round(t1 - t0)}ms, สร้าง PDF ${Math.round(performance.now() - t1)}ms`);

  const { name_1, branch_label } = data.customer;
  const raw = `${data.doc_no} ${name_1}${branch_label ? ` (${branch_label})` : ''}`;
  const baseName = raw.replace(/[/\\?%*:|"<>]/g, '-').trim();
  return { blob, filename: baseName + '.pdf', baseName };
}

// เริ่มโหลดตัวแปลงรูป (pdfjs + worker) ล่วงหน้า — เรียกตอนเปิดเมนู/ระหว่างรอสร้าง PDF
export const preloadQuotationImage = () => {
  import('../utils/pdfToImages').then(m => m.preloadPdfjs()).catch(() => {});
};

// PDF → รูป JPEG หน้าละรูป (ตัดพื้นที่ขาวท้ายหน้าสุดท้าย)
// makePng: สร้าง PNG ต่อทุกหน้าเป็นรูปเดียวสำหรับคลิปบอร์ด — สร้างตอนกดคัดลอกเท่านั้น (เข้ารหัส PNG ช้า)
async function pdfToImageFiles(pdfBlob, baseName) {
  const t0 = performance.now();
  const { pdfBlobToCanvases, canvasToBlob, stitchCanvases } = await import('../utils/pdfToImages');
  const canvases = await pdfBlobToCanvases(pdfBlob, { scale: 2, trimLastPage: true });
  const t1 = performance.now();
  const jpegs = await Promise.all(canvases.map(c => canvasToBlob(c, 'image/jpeg', 0.9)));
  console.info(`[qt-image] ${baseName}: วาดหน้า ${Math.round(t1 - t0)}ms, บีบอัด JPEG ${Math.round(performance.now() - t1)}ms (${canvases.length} หน้า)`);
  const files = jpegs.map((b, i) => new File(
    [b],
    jpegs.length > 1 ? `${baseName} (${i + 1}).jpg` : `${baseName}.jpg`,
    { type: 'image/jpeg' }
  ));
  let pngPromise = null;
  const makePng = () => (pngPromise ||= canvasToBlob(stitchCanvases(canvases), 'image/png'));
  return { files, makePng };
}

const fileToDataUrl = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = () => reject(reader.error);
  reader.readAsDataURL(file);
});

// doc — ดาวน์โหลดจากหน้าต่างไหน (หน้าตัวอย่าง PDF เป็นอีกหน้าต่างหนึ่ง)
const downloadFile = (file, doc = document) => {
  const url = URL.createObjectURL(file);
  const a = doc.createElement('a');
  a.href = url;
  a.download = file.name;
  doc.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60 * 1000);
};

const shareErrorAlert = () => Swal.fire({
  icon: 'error',
  title: 'สร้างรูปไม่สำเร็จ',
  text: 'ไม่สามารถสร้างรูปใบเสนอราคาได้ กรุณาลองใหม่อีกครั้ง',
  confirmButtonText: 'ตกลง',
  confirmButtonColor: '#1976d2',
});

const showLoading = (title) => {
  ensureQtPopupCss();
  Swal.fire({
    title,
    customClass: { popup: 'qt-pop' },
    allowOutsideClick: false,
    didOpen: () => Swal.showLoading(),
  });
};

// คลิปบอร์ดรูปภาพ/เมนูแชร์ ใช้ได้เฉพาะเว็บที่เป็น https — เปิดผ่าน http://IP จะไม่มีปุ่มเหล่านี้
const canCopyImage = () => !!(window.isSecureContext && navigator.clipboard?.write && window.ClipboardItem);

/**
 * popup แสดงตัวอย่างรูปก่อนส่ง — ปุ่มอยู่ใน popup (เป็นการกดของผู้ใช้โดยตรง เมนูแชร์/คลิปบอร์ดจึงไม่ถูกบล็อก)
 *  - แชร์ส่ง LINE: มือถือ/แท็บเล็ตที่รองรับการแชร์ไฟล์
 *  - คัดลอกรูป: คอมพิวเตอร์ → Ctrl+V ในแชท LINE
 *  - ดาวน์โหลด: ทุกเครื่อง
 */
async function showImagePreview({ files, makePng }, baseName) {
  ensureQtPopupCss();
  // data: URL ไม่ใช่ blob: — กดค้างที่รูปบน iPhone แล้ว "แชร์/บันทึก" จะได้ตัวรูปจริง
  // (ถ้าเป็น blob: iOS แชร์ออกไปเป็นข้อความลิงก์ blob:http://... ที่เปิดที่อื่นไม่ได้)
  const urls = await Promise.all(files.map(fileToDataUrl));
  const canShare = isTouchDevice() && !!navigator.canShare?.({ files });
  const canCopy = !isTouchDevice() && canCopyImage();
  const many = files.length > 1;
  const pages = urls.map((u, i) => `
    <img src="${u}" alt="${escapeHtml(files[i].name)}">
    ${many ? `<div class="qt-page">หน้า ${i + 1} / ${files.length}</div>` : ''}`
  ).join('');

  await Swal.fire({
    title: 'รูปใบเสนอราคา',
    width: 'min(720px, 96vw)',
    customClass: { popup: 'qt-pop' },
    showCloseButton: true,
    showConfirmButton: false,
    html: `
      <div class="qt-sub">${escapeHtml(baseName)}${many ? ` · ${files.length} หน้า` : ''}</div>
      ${isTouchDevice() && !canShare
        // เว็บที่ไม่ใช่ https (หรือเครื่องที่แชร์ไฟล์ไม่ได้) ไม่มีปุ่มแชร์ → บอกทางลัดของมือถือแทน
        ? '<div class="qt-sub" style="margin-top:6px;color:#2e7d32">กดค้างที่รูปเพื่อบันทึกหรือแชร์ส่ง LINE</div>'
        : ''}
      <div class="qt-preview" style="margin-top:12px">${pages}</div>
      <div class="qt-actions">
        ${canShare ? actionButton('qt-img-share', 'primary', 'share', 'แชร์ส่ง LINE') : ''}
        ${canCopy ? actionButton('qt-img-copy', 'primary', 'copy', 'คัดลอกรูป') : ''}
        ${actionButton('qt-img-download', 'outline', 'download', many ? `ดาวน์โหลด (${files.length} ไฟล์)` : 'ดาวน์โหลด')}
      </div>
      <div class="qt-msg" id="qt-img-msg"></div>`,
    didOpen: (popup) => {
      const msgEl = popup.querySelector('#qt-img-msg');
      const msg = (text, tone) => setQtMsg(msgEl, text, tone);
      popup.querySelector('#qt-img-download')?.addEventListener('click', () => {
        files.forEach(f => downloadFile(f));
        msg(many ? `ดาวน์โหลดแล้ว ${files.length} ไฟล์` : 'ดาวน์โหลดแล้ว', 'ok');
      });
      popup.querySelector('#qt-img-share')?.addEventListener('click', () => {
        navigator.share({ files })
          .then(() => Swal.close())
          .catch(err => {
            if (err.name === 'AbortError') return;
            console.error('share error:', err);
            msg('แชร์ไม่สำเร็จ กรุณาใช้ปุ่มดาวน์โหลดแทน', 'err');
          });
      });
      popup.querySelector('#qt-img-copy')?.addEventListener('click', () => {
        // ส่ง promise ให้ ClipboardItem เลย → เรียก write ได้ทันทีตอนกด แล้วค่อยรอสร้าง PNG
        // (ถ้ารอ PNG เสร็จก่อนค่อย write เบราว์เซอร์อาจไม่นับว่าเป็นการกดของผู้ใช้แล้ว)
        msg('กำลังคัดลอก...', 'info');
        navigator.clipboard.write([new window.ClipboardItem({ 'image/png': makePng() })])
          // เบราว์เซอร์รุ่นเก่ารับเฉพาะ Blob ไม่รับ promise → รอ PNG แล้วลองอีกครั้ง
          .catch(async () => navigator.clipboard.write([new window.ClipboardItem({ 'image/png': await makePng() })]))
          .then(() => msg(many
            ? 'คัดลอกแล้ว (รวมทุกหน้าเป็นรูปเดียว) — เปิดแชท LINE แล้วกด Ctrl+V'
            : 'คัดลอกแล้ว — เปิดแชท LINE แล้วกด Ctrl+V', 'ok'))
          .catch(err => {
            console.error('clipboard error:', err);
            msg('คัดลอกไม่สำเร็จ กรุณาใช้ปุ่มดาวน์โหลดแทน', 'err');
          });
      });
    },
  });
}

// PDF ที่สร้างแล้ว → รูป → popup ตัวอย่าง (มีกล่อง "กำลังสร้างรูป..." ระหว่างรอ)
async function shareImagesFromPdf(pdfBlob, baseName) {
  showLoading('กำลังสร้างรูปใบเสนอราคา...');
  let images;
  try {
    images = await pdfToImageFiles(pdfBlob, baseName);
  } catch (err) {
    console.error('❌ Error generating quotation image:', err);
    await shareErrorAlert();
    return;
  }
  await showImagePreview(images, baseName);
}

/**
 * ใบเสนอราคาเป็นรูป (1 หน้า = 1 รูป, ตัดพื้นที่ขาวท้ายหน้าสุดท้าย) → popup ตัวอย่าง
 * แล้วเลือก แชร์ส่ง LINE (มือถือ) / คัดลอกรูป (คอม) / ดาวน์โหลด
 */
async function shareQuotationImage(docNo, DocumentComponent, previewData = null) {
  showLoading('กำลังสร้างรูปใบเสนอราคา...');
  preloadQuotationImage(); // โหลดตัวแปลงรูปไปพร้อมกับดึงข้อมูล/สร้าง PDF
  let pdfResult;
  try {
    pdfResult = await buildQuotationPdf(docNo, DocumentComponent, previewData);
  } catch (err) {
    console.error('❌ Error generating quotation PDF:', err);
    await shareErrorAlert();
    return;
  }
  await shareImagesFromPdf(pdfResult.blob, pdfResult.baseName);
}

export const useShareQuotationImage = () => shareQuotationImage;

// เบราว์เซอร์บล็อกหน้าต่างใหม่ (เช่น Safari เมื่อเปิดหลังรอโหลดข้อมูล)
// → ให้ผู้ใช้แตะลิงก์เอง ซึ่งนับเป็นการกดของผู้ใช้ จึงไม่ถูกบล็อก
// คืน promise ที่จบเมื่อกล่องปิด — ผู้เรียกต่อ Swal อื่นหลังจากนี้จะได้ไม่ทับกล่องนี้
const showOpenLink = async (url, filename, pdfBlob, baseName) => {
  ensureQtPopupCss();
  setTimeout(() => URL.revokeObjectURL(url), 10 * 60 * 1000);
  let wantImage = false;
  await Swal.fire({
    icon: 'success',
    title: 'ใบเสนอราคาพร้อมแล้ว',
    width: 'min(580px, 96vw)',
    customClass: { popup: 'qt-pop' },
    showCloseButton: true,
    showConfirmButton: false,
    html: `
      <div class="qt-sub">${escapeHtml(baseName)}</div>
      <div class="qt-opts" style="margin-top:16px">
        <a id="pdf-open-link" class="qt-opt pdf" href="${url}" target="_blank" rel="noopener" download="${escapeHtml(filename)}" style="text-decoration:none">
          <span class="qt-opt-ic">${icon('pdf', 26)}</span>
          <span class="qt-opt-body"><div class="qt-opt-t">ไฟล์ PDF</div><div class="qt-opt-s">ดาวน์โหลด · พิมพ์</div></span>
          <span class="qt-opt-go"></span>
        </a>
        ${optionCard('pdf-image-btn', 'img', 'image', 'รูปภาพ', 'ดาวน์โหลด · แชร์ส่ง LINE')}
      </div>`,
    didOpen: (popup) => {
      popup.querySelector('#pdf-open-link')?.addEventListener('click', () => setTimeout(() => Swal.close(), 300));
      popup.querySelector('#pdf-image-btn')?.addEventListener('click', () => { wantImage = true; Swal.close(); });
    },
  });
  if (wantImage) await shareImagesFromPdf(pdfBlob, baseName);
};

const TOOLBAR_BTN = 'padding:8px 14px;border:0;border-radius:6px;color:#fff;text-decoration:none;font:600 14px sans-serif;cursor:pointer;white-space:nowrap;box-shadow:0 2px 6px rgba(0,0,0,.2)';

// ปุ่ม "ดาวน์โหลดรูปภาพ/บันทึก-ส่งรูปภาพ" ในหน้าตัวอย่าง — ทำงานในหน้าต่างตัวอย่าง (win)
// getImages: คืน promise ของ { files } (สร้างครั้งแรกที่กด แล้วใช้ซ้ำ)
const wireImageButton = (win, btn, getImages) => {
  const label = btn.textContent;
  btn.addEventListener('click', async () => {
    let images;
    try {
      btn.textContent = 'กำลังสร้างรูป...';
      btn.disabled = true;
      images = await getImages();
    } catch (err) {
      console.error('❌ Error generating quotation image:', err);
      win.alert('ไม่สามารถสร้างรูปใบเสนอราคาได้ กรุณาลองใหม่อีกครั้ง');
      return;
    } finally {
      btn.textContent = label;
      btn.disabled = false;
    }
    // มือถือ: เมนูแชร์มี "บันทึกรูป" และ LINE ให้เลือก — รูปสร้างไว้แล้วตอนเปิดหน้า จึงยังนับเป็นการกดของผู้ใช้
    const files = images.files.map(f => new win.File([f], f.name, { type: f.type }));
    if (isTouchDevice() && win.navigator.canShare?.({ files })) {
      win.navigator.share({ files }).catch(err => {
        if (err.name !== 'AbortError') files.forEach(f => downloadFile(f, win.document));
      });
      return;
    }
    files.forEach(f => downloadFile(f, win.document));
  });
};

/**
 * previewPDF(docNo, DocumentComponent):
 *   1. POST to `/preview-qt` (with { doc_no })
 *   2. Build a blob via @react-pdf/renderer using the passed-in DocumentComponent
 *   3. Open it in a new window with download buttons (PDF + รูปภาพ)
 *      — desktop: PDF in an <iframe>; mobile/tablet: the pages as images
 *
 * ต้องเรียกตรงจากการกดของผู้ใช้ (ก่อน await อื่น) ไม่งั้นเบราว์เซอร์บล็อกหน้าต่างใหม่ → ใช้ showOpenLink แทน
 */
async function previewPDF(docNo, DocumentComponent, previewData = null) {
  let url;
  // หน้าตัวอย่างมีปุ่มรูปภาพ (มือถือแปลงเป็นรูปทันที) → โหลดตัวแปลงรูปไปพร้อมกัน
  preloadQuotationImage();
  // เปิดหน้าต่างทันทีตอนกด ก่อน await ใด ๆ — Safari บล็อก window.open ที่เรียกหลังรอโหลดข้อมูล
  let previewWin = null;
  try {
    previewWin = window.open('', '_blank');
    if (previewWin) {
      previewWin.document.title = 'กำลังสร้าง PDF...';
      previewWin.document.body.innerHTML =
        '<p style="font-family:sans-serif;text-align:center;margin-top:40vh;color:#555">กำลังสร้าง PDF...</p>';
    }
  } catch (e) {
    previewWin = null;
  }

  try {
    const { blob, filename, baseName } = await buildQuotationPdf(docNo, DocumentComponent, previewData);

    url = URL.createObjectURL(blob);
    if (!previewWin || previewWin.closed) {
      await showOpenLink(url, filename, blob, baseName);
      return;
    }

    const win = previewWin;
    const doc = win.document;
    const touch = isTouchDevice();
    win.addEventListener('unload', () => URL.revokeObjectURL(url));

    // มือถือ: แปลงเป็นรูปเลยตอนเปิดหน้า (ใช้แสดงแทน PDF และพร้อมแชร์ทันทีที่กด)
    let images = null;
    if (touch) {
      try {
        images = await pdfToImageFiles(blob, baseName);
      } catch (err) {
        console.error('❌ Error generating quotation image:', err);
        win.location.href = url; // แปลงไม่ได้ → เปิด PDF ตรง ๆ แบบเดิม
        return;
      }
      if (win.closed) return;
    }
    let imagesPromise = images ? Promise.resolve(images) : null;
    const getImages = () => {
      if (!imagesPromise) {
        imagesPromise = pdfToImageFiles(blob, baseName).catch(err => { imagesPromise = null; throw err; });
      }
      return imagesPromise;
    };

    doc.title = filename;
    doc.body.style.margin = '0';
    const toolbar = `
      <div style="position:fixed;top:10px;right:10px;display:flex;gap:8px;z-index:10">
        <a href="${url}" download="${escapeHtml(filename)}" style="${TOOLBAR_BTN};background:#1976d2">ดาวน์โหลด PDF</a>
        <button id="qt-image-btn" type="button" style="${TOOLBAR_BTN};background:#2e7d32">
          ${touch ? 'บันทึก/ส่งรูปภาพ' : 'ดาวน์โหลดรูปภาพ'}</button>
      </div>`;

    if (touch) {
      // data: URL — กดค้างที่รูปแล้วแชร์/บันทึกได้ตัวรูปจริง (ดู showImagePreview)
      const dataUrls = await Promise.all(images.files.map(fileToDataUrl));
      if (win.closed) return;
      const imgs = dataUrls.map((u, i) =>
        `<img src="${u}" alt="${escapeHtml(images.files[i].name)}" style="display:block;width:100%;margin-bottom:8px;box-shadow:0 1px 4px rgba(0,0,0,.2)">`
      ).join('');
      doc.head.insertAdjacentHTML('beforeend', '<meta name="viewport" content="width=device-width, initial-scale=1">');
      doc.body.style.background = '#eee';
      doc.body.innerHTML = `${toolbar}<div style="padding:60px 8px 8px">${imgs}</div>`;
    } else {
      doc.body.innerHTML = `
        <iframe src="${url}" style="border:none; width:100%; height:100vh;"></iframe>
        ${toolbar}`;
    }
    wireImageButton(win, doc.getElementById('qt-image-btn'), getImages);
    // คอม: แปลงรูปเตรียมไว้เลยระหว่างผู้ใช้ดู PDF — กดดาวน์โหลดรูปแล้วได้ทันที (พลาดก็ไม่เป็นไร กดแล้วลองใหม่เอง)
    if (!touch) getImages().catch(() => {});
  } catch (err) {
    if (url) URL.revokeObjectURL(url);
    if (previewWin && !previewWin.closed) previewWin.close();
    console.error('❌ Error generating PDF preview:', err);
    Swal.fire({
      icon: 'error',
      title: 'โหลดข้อมูลไม่สำเร็จ',
      text: 'ไม่สามารถสร้าง PDF ได้ กรุณาลองใหม่อีกครั้ง',
      confirmButtonText: 'ตกลง',
      confirmButtonColor: '#1976d2',
    });
  }
}

export const usePreviewPDF = () => previewPDF;

const copyText = (text) => {
  if (navigator.clipboard?.writeText && window.isSecureContext) return navigator.clipboard.writeText(text);
  const el = document.createElement('textarea');
  el.value = text;
  el.style.cssText = 'position:fixed;opacity:0';
  document.body.appendChild(el);
  el.select();
  document.execCommand('copy');
  el.remove();
  return Promise.resolve();
};

/**
 * popup หลังสร้าง/บันทึกใบเสนอราคา — ไม่เปิด PDF เอง ให้เลือกส่งเป็น "ไฟล์ PDF" หรือ "รูปภาพ"
 *  - ไฟล์ PDF: เปิดหน้า PDF (แท็บใหม่) แล้วปิด popup นี้ — หน้า PDF มีปุ่มดาวน์โหลดรูปภาพอยู่แล้ว
 *  - รูปภาพ: ไป popup ตัวอย่างรูป
 * คืน promise ที่จบเมื่อผู้ใช้ปิดทุก popup แล้ว
 */
export async function showQuotationDone(docNo, DocumentComponent, { title = 'สร้างใบเสนอราคาสำเร็จ' } = {}) {
  ensureQtPopupCss();
  preloadQuotationImage();
  let wantImage = false;
  await Swal.fire({
    icon: 'success',
    title,
    width: 'min(580px, 96vw)',
    customClass: { popup: 'qt-pop' },
    allowOutsideClick: false,
    confirmButtonText: 'เสร็จสิ้น',
    confirmButtonColor: '#1976d2',
    html: `
      <div class="qt-doc">
        <div>
          <div class="qt-doc-lbl">เลขที่เอกสาร</div>
          <div class="qt-doc-no">${escapeHtml(docNo)}</div>
        </div>
        <button type="button" class="qt-iconbtn" id="qt-copy-no" title="คัดลอกเลขที่เอกสาร">${icon('copy', 20)}</button>
      </div>
      <div class="qt-msg" id="qt-done-msg" style="margin-top:8px"></div>
      <div class="qt-label" style="margin-top:8px">ส่งให้ลูกค้าเป็น</div>
      <div class="qt-opts">
        ${optionCard('qt-opt-pdf', 'pdf', 'pdf', 'ไฟล์ PDF', 'ดาวน์โหลด · พิมพ์')}
        ${optionCard('qt-opt-img', 'img', 'image', 'รูปภาพ', 'ดาวน์โหลด · แชร์ส่ง LINE')}
      </div>`,
    didOpen: (popup) => {
      const msgEl = popup.querySelector('#qt-done-msg');
      popup.querySelector('#qt-copy-no')?.addEventListener('click', () => {
        copyText(docNo)
          .then(() => setQtMsg(msgEl, 'คัดลอกเลขที่เอกสารแล้ว', 'ok'))
          .catch(() => setQtMsg(msgEl, 'คัดลอกไม่สำเร็จ', 'err'));
      });
      const pdfCard = popup.querySelector('#qt-opt-pdf');
      pdfCard?.addEventListener('click', () => {
        // เรียกตรงจากการกด → หน้าต่าง PDF ไม่ถูกบล็อก แล้วปิด popup นี้เลย
        // (ไม่ await — previewPDF ทำงานต่อในแท็บใหม่ ถ้าผิดพลาดจะขึ้นแจ้งเตือนของมันเอง)
        previewPDF(docNo, DocumentComponent);
        Swal.close();
      });
      popup.querySelector('#qt-opt-img')?.addEventListener('click', () => {
        wantImage = true;
        Swal.close();
      });
    },
  });
  if (wantImage) await shareQuotationImage(docNo, DocumentComponent);
}
